<template lang="pug">
.app-table
  AppTableLoading(
    v-if="loading"
    :columns="columnsCount"
    :rows="rowsCount"
  )
  //- We must use v-show below to allow responsive table dropdown actions to execute callbacks.
  //- Otherwise excess negative margin may be applied to the table container.
  //- See app/assets/javascripts/responsive-tables.js
  table.table(
    v-show="!loading"
    :class="allTableClasses"
  )
    template(v-if="hasColumnComponents")
      colgroup
        col(v-if="selectable")
      colgroup
        template(v-for="(columnComponent, columnComponentKey) in visibleColumnComponents")
          col(
            :key="`columnComponent-${columnComponentKey}`"
            :class="columnComponent.colClasses"
          )
    thead
      tr
        th(v-if="selectable")
          AppCheckbox(
            v-if="crossPageSelectable"
            :value="selectAllChecked"
            :disabled="isEmpty"
            :indeterminate="selectAllIndeterminate"
            @input="onToggleSelectAllCrossPage"
          )
          AppCheckbox(
            v-else
            :key="checkboxKey"
            :disabled="isEmpty"
            :indeterminate="someSelected && !allSelected"
            :value="allSelected"
            @input="onToggleSelectAll"
          )
        template(v-if="hasColumnComponents")
          template(v-for="(columnComponent, columnComponentKey) in visibleColumnComponents")
            SlotForwarder(
              v-if="columnComponent.$scopedSlots && columnComponent.$scopedSlots.header"
              :key="`columnComponent-${columnComponentKey}`"
              tag="th"
              slot-name="header"
              :class="columnComponent.headerClasses"
              :component="columnComponent"
            )
            th(
              v-else
              :key="`columnComponent-${columnComponentKey}`"
              :class="thClasses(columnComponent)"
              @click="sort(columnComponent)"
            )
              span.cursor-pointer(v-if="columnComponent.sortBy")
                | {{ columnComponent.header }}
                AppTooltipIcon(
                  v-if="columnComponent.headerTooltip"
                  :message="columnComponent.headerTooltip"
                  :type="columnComponent.headerTooltipType"
                  :size="columnComponent.headerTooltipSize"
                  :class="columnComponent.headerTooltipClasses"
                  placement="top"
                )
                i(:class="sortArrowClasses(columnComponent)")
              template(v-else-if="columnComponent.headerDropdown")
                AppToggleDropdown(
                  trigger-classes="color-inherit"
                  dropdown-classes="table-actions-dropdown"
                  :extra-classes="columnComponent.headerDropdownExtraClasses"
                )
                  span.clickable.hover-visible-container(slot="trigger")
                    | {{ columnComponent.header }}
                    i.hi-edit.hover-visible.text-blue.ml-10
                  component(
                    :is="columnComponent.headerDropdownComponent"
                    :about-fields="columnComponent.headerDropdownFields"
                    @change="handleHeaderComponentChange"
                  )
              template(v-else)
                | {{ columnComponent.header }}
                AppTooltipIcon(
                  v-if="columnComponent.headerTooltip"
                  :message="columnComponent.headerTooltip"
                  :type="columnComponent.headerTooltipType"
                  :size="columnComponent.headerTooltipSize"
                  :class="columnComponent.headerTooltipClasses"
                  placement="top"
                )
        template(v-else-if="hasTableColumns")
          th(
            v-for="(column, columnKey) in visibleTableColumns"
            :key="`column-${columnKey}`"
          )
            | {{ column.header }}
    tbody(v-if="hasData")
      tr(
        v-for="(row, key) in tableData"
        :key="`row-${key}`"
        :class="trClasses(row)"
        @click="clickRow($event, row)"
      )
        td.app-table-exclude(v-if="selectable")
          AppCheckbox(
            v-if="crossPageSelectable"
            :value="isSelectedCrossPage(row)"
            :disabled="isDisabled(row)"
            @input="onToggleSelectedCrossPage(row)"
          )
          AppCheckbox(
            v-else
            :value="isSelected(row)"
            :disabled="isDisabled(row)"
            @input="onToggleSelected(row)"
          )
        template(v-if="hasColumnComponents")
          template(v-for="(columnComponent, columnComponentKey) in visibleColumnComponents")
            SlotForwarder(
              :key="`columnComponent-${columnComponentKey}`"
              :data-label="columnComponent.header"
              :component="columnComponent"
              :props="{ row, key }"
              :class="`${columnComponent.cellClasses} ${tdClasses}`"
            )
        template(v-else)
          td(
            v-for="(column, columnKey) in visibleTableColumns"
            :key="`column-${columnKey}`"
          )
            | {{ columnValue(row, column) }}
    AppTableLoadingBody(
      v-if="showLazyLoading"
      :columns="columnsCount"
      :rows="LOADING_ROWS_COUNT"
    )
  slot(
    v-if="isEmpty && !loading"
    name="empty"
  )
    .bold.text-center No data
  slot
</template>

<script>
import { get, negate } from 'lodash-es';
import MembersAboutFields from '~/components/members/MembersAboutFields.vue';
import MembersInfoFields from '~/components/members/MembersInfoFields.vue';
import MembersEmploymentFields from '~/components/members/MembersEmploymentFields.vue';
import AppTableLoadingBody from '~/components/AppTableLoadingBody.vue';

const LOADING_ROWS_COUNT = 4;
// do not display lazy select animation before the page fold
const MIN_ITEMS_FOR_LAZY_LOADING = 10;

export default {
  name: 'AppTable',
  components: {
    SlotForwarder: {
      props: {
        tag: {
          type: String,
          default: 'td',
        },
        props: {
          type: Object,
        },
        slotName: {
          type: String,
          default: 'default',
        },
        component: {
          type: Object,
          validator: (component) => component._isVue,
        },
      },
      render(createElement) {
        return createElement(this.tag, {}, this.component.$scopedSlots[this.slotName](this.props));
      },
    },
    MembersAboutFields,
    MembersInfoFields,
    MembersEmploymentFields,
    AppTableLoadingBody,
  },
  provide() {
    return {
      $table: this,
    };
  },
  model: {
    event: 'selected',
  },
  props: {
    tableData: {
      type: Array,
      default: () => [],
    },
    tableColumns: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    lazyLoading: {
      type: Boolean,
      default: false,
    },
    tableClasses: {
      type: String,
      default: '',
    },
    tdClasses: {
      type: String,
      default: '',
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    // extra flag to support select cross page, keep selection after pager navigation
    crossPageSelectable: {
      type: Boolean,
      default: false,
    },
    hoverable: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [Array, Object],
      default: () => [],
    },
    altField: {
      type: String,
      default: '',
    },
    customTrClasses: {
      type: Array,
      default: () => [],
    },
    pageFold: {
      type: Number,
      default: MIN_ITEMS_FOR_LAZY_LOADING,
    },
  },
  data: () => ({
    columnComponents: [],
    selected: [],
    selectAllChecked: false, // when crossPageSelectable
    // when crossPageSelectable
    // {id: bool},
    //  when selectAllChecked=true, here ids to exclude
    //  when selectAllChecked=false, here only ids to include
    toggledIds: {},
    selectAllIndeterminate: false, // when crossPageSelectable
    LOADING_ROWS_COUNT,
  }),
  computed: {
    allTableClasses() {
      const tableClasses = [];

      if (this.selectable) {
        tableClasses.push('has-batch-actions');
      }

      if (this.hoverable) {
        tableClasses.push('table-hover');
      }

      return this.tableClasses + ' ' + tableClasses.join(' ');
    },
    hasTableColumns() {
      return Array.isArray(this.tableColumns) && this.tableColumns.length > 0;
    },
    hasColumnComponents() {
      return this.columnComponents.length > 0;
    },
    hasData() {
      return this.tableData && this.tableData.length > 0;
    },
    isEmpty() {
      return !this.hasData;
    },
    allSelected() {
      return this.someSelected && this.selected.length === this.tableData.length;
    },
    showLazyLoading() {
      return this.lazyLoading && this.tableData && this.tableData.length > this.pageFold;
    },
    someSelected() {
      return this.selected.length > 0;
    },
    checkboxKey() {
      return `${this.someSelected}${this.allSelected}`;
    },
    columnsCount() {
      return this.hasTableColumns ? this.tableColumns.length : this.columnComponents.length;
    },
    rowsCount() {
      if (this.tableData && this.tableData.length > 0) {
        // display either one page of skeleton or the same amount of rows for sorting
        return this.tableData.length > LOADING_ROWS_COUNT ? LOADING_ROWS_COUNT : this.tableData.length;
      }

      return LOADING_ROWS_COUNT;
    },
    visibleTableColumns() {
      if (this.hasTableColumns) {
        return this.tableColumns.filter((tableColumn) =>
          typeof tableColumn.visible === 'boolean' ? tableColumn.visible : true,
        );
      }

      return null;
    },
    visibleColumnComponents() {
      return this.columnComponents.filter((columnComponent) => columnComponent.visible);
    },
  },
  watch: {
    tableData() {
      if (!this.crossPageSelectable) {
        this.clearSelected();
      }
    },
    value: {
      immediate: true,
      handler(newVal) {
        if (this.crossPageSelectable) {
          if (newVal.length === 0) {
            this.resetCrossPageSelectState();
          }
        } else {
          this.selected = newVal;
        }
      },
    },
  },
  methods: {
    clickRow(e, row) {
      if (!e.target.closest('.app-table-exclude')) {
        this.$emit('clickRow', row);
      }
    },
    columnValue(row, column) {
      return row[column.key];
    },
    registerColumn(columnComponent) {
      this.columnComponents.push(columnComponent);
    },
    isSelected(record) {
      return this.selected.indexOf(record) >= 0;
    },
    resetCrossPageSelectState(allChecked) {
      this.selectAllChecked = allChecked;
      this.selectAllIndeterminate = false;
      this.toggledIds = {};
    },
    onToggleSelectAllCrossPage() {
      this.resetCrossPageSelectState(!this.selectAllChecked);
      this.onSelectedCrossPage();
    },
    isSelectedCrossPage(record) {
      return this.selectAllChecked ? !this.toggledIds[record.id] : this.toggledIds[record.id];
    },
    onToggleSelectedCrossPage(record) {
      if (this.toggledIds[record.id]) {
        delete this.toggledIds[record.id];
      } else {
        this.toggledIds[record.id] = true;
      }
      this.selectAllIndeterminate = Object.keys(this.toggledIds).length > 0;
      this.onSelectedCrossPage();
    },
    isDisabled(record) {
      return !!record.disabled;
    },
    onToggleSelectAll() {
      if (this.selected.length) {
        this.clearSelected();
      } else {
        this.selected = this.tableData.filter(negate(this.isDisabled));
        this.onSelected();
      }
    },
    onToggleSelected(record) {
      const recordIndex = this.selected.indexOf(record);

      if (recordIndex >= 0) {
        this.selected.splice(recordIndex, 1);
      } else {
        this.selected.push(record);
      }

      this.onSelected();
    },
    clearSelected() {
      this.selected = [];
      this.onSelected();
    },
    onSelected() {
      this.$emit('selected', this.selected);
    },
    onSelectedCrossPage() {
      const ids = Object.keys(this.toggledIds).map(Number);

      this.$emit('selected', this.selectAllChecked ? { all: true, except: ids } : ids);
    },
    meetsAltRowCriteria(row) {
      return this.altField && get(row, this.altField, false);
    },
    thClasses({ headerClasses, sortBy }) {
      return [headerClasses, sortBy && sortBy.field ? 'sortable hover-visible-container' : ''].join(' ');
    },
    sort({ sortBy, name }) {
      if (sortBy?.field) {
        this.$emit('sort', {
          field: name || sortBy.field,
          direction: sortBy.direction === 'desc' ? 'asc' : 'desc',
        });
      }
    },
    sortArrowClasses({ sortBy, name }) {
      const chevrons = {
        [false]: 'fa-caret-down is-muted hover-visible',
        asc: 'fa-caret-up',
        desc: 'fa-caret-down',
      };
      const chevron = chevrons[sortBy.field === name && !!sortBy.direction && sortBy.direction];

      return ['fa', chevron, 'ml-5'];
    },
    trClasses(row) {
      return [{ 'row-alt': this.meetsAltRowCriteria(row) || this.isDisabled(row) }, ...[row.trClasses || []]];
    },
    handleHeaderComponentChange(payload) {
      this.$emit('header-component-change', payload);
    },
  },
};
</script>
